import { useMsal } from "@azure/msal-react";
import { ActionIcon, Flex, SegmentedControl, Switch, Text, TextInput } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import {
    MantineReactTable,
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_RowVirtualizer,
    type MRT_SortingState,
    useMantineReactTable,
} from "mantine-react-table";
import { type UIEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CTS_USER_CHAT } from "src/db";
import { useSearchAfterPagination } from "src/hooks/useSearchAfterPagination";
import { useUser } from "src/hooks/useUser";
import { postAPI } from "src/utils/fetch";
import { useAdmin } from "src/utils/useAdmin";
import { useEvaluator } from "src/utils/useEvaluator";
import type { FeedbackSchema } from "./MetadataFeedback/types";
import { PdfFocusProvider } from "./PDFViewer/context";
import SMEFeedbackModal from "./SMEFeedbackModal";
import TopToolbarCustomActions from "./TopToolbarCustomActions";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

// Define all columns
const feedbackColumns: MRT_ColumnDef<FeedbackSchema>[] = [
    {
        id: "feedback__timestamp",
        accessorKey: "_source.timestamp",
        accessorFn: (originalRow) => {
            if (!originalRow._source) return "";
            const dateValue = originalRow._source?.timestamp
                ? new Date(originalRow._source.timestamp)
                : new Date("2000-01-01T00:00:00.000Z");
            return dateValue;
        },
        header: "Timestamp",
        filterVariant: "date-range",
        sortingFn: "datetime",
        size: 200,
        Cell: ({ cell }) => {
            const dateValue = cell.getValue<Date>();
            if (
                dateValue &&
                dateValue.getTime() !== new Date("2000-01-01T00:00:00.000Z").getTime()
            ) {
                return dateValue.toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                });
            }
            return "N/A";
        },
        Header: ({ column }) => <em>{column.columnDef.header}</em>,
    },
    {
        id: "feedback__question",
        accessorKey: "_source.question",
        accessorFn: (originalRow) => {
            return originalRow._source ? originalRow._source.question || "N/A" : "N/A";
        },
        header: "Question",
        filterFn: "includesString",
        size: 300,
    },
    {
        id: "feedback__cts_answer.answer",
        accessorKey: "_source.cts_answer.answer",
        accessorFn: (originalRow) => {
            return originalRow._source ? originalRow._source.cts_answer.answer || "N/A" : "N/A";
        },
        header: "Answer Preview",
        filterFn: "includesString",
        size: 250,
        maxSize: 300,
        Cell: ({ cell }) => {
            const value = cell.getValue<string>();
            if (value === "N/A") return value;

            // Prepare full text for title attribute (first 500 chars)
            const fullTextForTitle = value
                .replace(/[#*`[\]()]/g, "")
                .replace(/\n+/g, " ")
                .trim()
                .substring(0, 500);

            // Try to extract content after first ## heading
            const headingMatch = value.match(/##\s*([^#\n]+)\n+([^#]*?)(?=##|$)/);
            if (headingMatch?.[2]) {
                // Found a ## heading, extract the content that follows it
                const contentAfterHeading = headingMatch[2]
                    .replace(/[#*`[\]()]/g, "")
                    .replace(/\n+/g, " ")
                    .trim();
                const truncated =
                    contentAfterHeading.length > 200
                        ? `${contentAfterHeading.substring(0, 200)}...`
                        : contentAfterHeading;
                return <span title={fullTextForTitle}>{truncated}</span>;
            }

            // Fallback: Strip markdown and truncate to 150 characters
            const plainText = value.replace(/[#*`[\]()]/g, "").trim();
            const truncated =
                plainText.length > 150 ? `${plainText.substring(0, 150)}...` : plainText;
            return <span title={fullTextForTitle}>{truncated}</span>;
        },
    },
    {
        id: "feedback__cts_answer.feedback_type",
        accessorKey: "_source.cts_answer.feedback_type",
        accessorFn: (originalRow) => {
            return originalRow._source
                ? originalRow._source.cts_answer?.feedback_type || "N/A"
                : "N/A";
        },
        header: "User Feedback",
        filterFn: "includesString",
        maxSize: 150,
    },
    {
        id: "feedback__evaluations.sme.feedback_type",
        accessorKey: "_source.evaluations.sme.feedback_type",
        accessorFn: (originalRow) => {
            return originalRow._source
                ? originalRow._source.evaluations?.sme?.feedback_type || "N/A"
                : "N/A";
        },
        header: "Feedback Type",
        filterFn: "includesString",
        maxSize: 150,
    },
    {
        id: "feedback__evaluations.sme.comments",
        accessorKey: "_source.evaluations.sme.comments",
        accessorFn: (originalRow) => {
            const comments = originalRow._source?.evaluations?.sme?.comments;
            return comments || "N/A";
        },
        header: "Feedback Comments",
        filterFn: "includesString",
        size: 250,
    },
    {
        id: "feedback__org_code",
        accessorKey: "_source.org_code",
        accessorFn: (originalRow) => {
            const orgCode = originalRow._source?.org_code;
            return orgCode || "N/A";
        },
        header: "Organization",
        filterFn: "includesString",
        size: 250,
    },
    {
        id: "feedback__origin",
        accessorKey: "_source.origin",
        accessorFn: (originalRow) => {
            const orgCode = originalRow._source?.origin;
            return orgCode || "N/A";
        },
        header: "Origin",
        filterFn: "includesString",
        size: 250,
    },
];

const columnsMap: Record<"cts_user_chat", MRT_ColumnDef<FeedbackSchema>[]> = {
    cts_user_chat: feedbackColumns as MRT_ColumnDef<FeedbackSchema>[],
};

const SMEFeedback = () => {
    // biome-ignore lint/correctness/useExhaustiveDependencies: CTS_USER_CHAT is intentionally included
    const columns = useMemo(() => columnsMap[CTS_USER_CHAT] ?? [], [CTS_USER_CHAT]);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const rowVirtualizerInstanceRef =
        useRef<MRT_RowVirtualizer<HTMLDivElement, HTMLTableRowElement>>(null);

    const [userEncoded, setUserEncoded] = useState<string>("");
    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState<string>("");
    const [filterMode, setFilterMode] = useState<string>("to_review"); // "to_review", "reviewed"
    const [myQuestionsOnly, setMyQuestionsOnly] = useState<boolean>(false); // Toggle for filtering by user email

    // Admin-only state for reviewing other users' Q&As
    const [reviewUserEmail, setReviewUserEmail] = useState<string>("");
    const [reviewUserEncoded, setReviewUserEncoded] = useState<string>("");

    const user = useUser();

    useEffect(() => {
        if (user.data) {
            const encoded = window.btoa(user.data.info.email);
            setUserEncoded(encoded);
        }
    }, [user]);

    // Handle admin email input for reviewing other users (admin-only)
    const handleReviewUserEmail = useCallback((email: string) => {
        setReviewUserEmail(email);
        if (email.trim()) {
            // Basic email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (emailRegex.test(email.trim())) {
                const encoded = window.btoa(email.trim());
                setReviewUserEncoded(encoded);
            } else {
                setReviewUserEncoded("");
            }
        } else {
            setReviewUserEncoded("");
        }
    }, []);

    // Clear review user (admin-only)
    const clearReviewUser = useCallback(() => {
        setReviewUserEmail("");
        setReviewUserEncoded("");
    }, []);

    const isEvaluator = useEvaluator();
    const isAdmin = useAdmin();

    useEffect(() => {
        if (isEvaluator === undefined) {
            return;
        }
        if (!isEvaluator) {
            navigate({ to: "/unauthorized" });
        }
    }, [isEvaluator, navigate]);

    const { instance } = useMsal();

    // Get current URL parameters
    const location = useLocation();
    const router = useRouter();
    const selectedId = location.search?.id;

    const activeUserEncoded = useMemo(() => {
        if (isAdmin && reviewUserEncoded) {
            return reviewUserEncoded;
        }

        if (myQuestionsOnly) {
            return userEncoded;
        }

        return "";
    }, [isAdmin, reviewUserEncoded, myQuestionsOnly, userEncoded]);

    const buildPayload = useCallback(() => {
        const payload =
            columnFilters
                ?.filter((val) =>
                    Array.isArray(val.value)
                        ? val.value.filter(Boolean).length > 0
                        : Boolean(val.value),
                )
                .filter((filter) => {
                    const key = filter.id.split("__")[1] ?? filter.id;
                    return key !== "evaluated";
                })
                .map((filter) => {
                    const key = filter.id.split("__")[1] ?? filter.id;

                    let value = filter.value;
                    if (key === "timestamp" && Array.isArray(value)) {
                        value = value.map((item) => (item == null ? "" : item));
                    }

                    return {
                        key,
                        value,
                    };
                }) ?? [];

        if (globalFilter?.trim()) {
            payload.push({ key: "_all", value: globalFilter.trim() });
        }

        if (filterMode === "to_review") {
            payload.push({ key: "evaluations.sme.status", value: "incomplete" });
        } else if (filterMode === "reviewed") {
            payload.push({ key: "evaluations.sme.status", value: "complete" });
        }

        if (activeUserEncoded) {
            payload.push({ key: "user", value: [activeUserEncoded] });
        }

        return payload;
    }, [columnFilters, globalFilter, filterMode, activeUserEncoded]);

    const { data, fetchNextPage, isFetching, isLoading, isError, documentId } =
        useSearchAfterPagination({
            queryKey: [
                "sme-feedback",
                filterMode,
                CTS_USER_CHAT,
                sorting,
                columnFilters,
                globalFilter || null,
                activeUserEncoded,
                myQuestionsOnly,
            ],
            index: CTS_USER_CHAT,
            sorting,
            columnFilters,
            globalFilter,
            buildPayload,
            resetDependencies: [filterMode, activeUserEncoded, myQuestionsOnly],
        });

    // Flatten data and deduplicate by _id
    const flatData = useMemo(() => {
        if (isError) {
            return [];
        }

        if (!data || !data.pages || data.pages.length === 0) {
            return [];
        }

        // biome-ignore lint/suspicious/noExplicitAny: Allow 'any' here for flexibility
        const allData = data.pages.flatMap((page: any) => {
            // biome-ignore lint/suspicious/noExplicitAny: Allow 'any' here for flexibility
            return page.hits.hits.map((doc: any) => ({
                ...doc,
                fields: doc._source.metadata,
            }));
        });

        // Deduplicate by _id to prevent duplicate key warnings
        const seen = new Set<string>();
        const deduplicatedData = allData.filter((item) => {
            if (item._id && seen.has(item._id)) {
                return false; // Skip duplicate
            }
            if (item._id) {
                seen.add(item._id);
            }
            return true;
        });

        return deduplicatedData;
    }, [data, isError]);

    // Simple query to fetch specific feedback item by ID when not in loaded data
    const { data: specificFeedbackData } = useQuery({
        queryKey: ["specific-sme-feedback", selectedId],
        queryFn: async () => {
            const PATH = `v1/search/${CTS_USER_CHAT}/metadata/docs?max_result=1`;
            const url = new URL(PATH, BACKEND_URL);
            // biome-ignore lint/suspicious/noExplicitAny: Payload structure requires mixed types
            const payload: any[] = [{ key: "_id", value: selectedId }];
            // Only add user filter if activeUserEncoded is not empty
            if (activeUserEncoded) {
                payload.push({ key: "user", value: activeUserEncoded });
            }
            const response = await postAPI({
                url: url.toString(),
                instance,
                // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                payload: payload as any,
            });
            if (!response) {
                console.warn("No response received for specific feedback query");
                return null;
            }
            const data = await response.json();
            return data?.hits?.hits?.[0] || null;
        },
        enabled: Boolean(selectedId && !flatData.find((doc) => doc._id === selectedId)),
        refetchOnWindowFocus: false,
    });

    // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
    const totalDBRowCount = (data as any)?.pages?.[0]?.hits?.total?.value ?? 0;

    // Use count directly from main query response
    const displayCount = totalDBRowCount;

    const fetchMoreOnBottomReached = useCallback(
        (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
                if (
                    scrollHeight - scrollTop - clientHeight < 400 &&
                    !isFetching &&
                    flatData.length < totalDBRowCount &&
                    flatData.length > 0 // Only fetch more if we already have data
                ) {
                    // Only attempt to fetch next page if we have more data available
                    // The fetchNextPage will handle documentId validation internally
                    fetchNextPage();
                }
            }
        },
        [fetchNextPage, isFetching, flatData.length, totalDBRowCount],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: sorting is required for correct behavior
    useEffect(() => {
        if (rowVirtualizerInstanceRef.current) {
            try {
                rowVirtualizerInstanceRef.current.scrollToIndex(0);
            } catch (e) {
                console.error(e);
            }
        }
    }, [sorting]);

    useEffect(() => {
        fetchMoreOnBottomReached(tableContainerRef.current);
    }, [fetchMoreOnBottomReached]);

    const [rowVirtualizerRef, setRowVirtualizerRef] = useState<MRT_RowVirtualizer<
        HTMLDivElement,
        HTMLTableRowElement
    > | null>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null); // we can get access to the underlying TableContainer element and react to its scroll events

    // Find selected row based on URL parameter
    const selectedRow = useMemo(() => {
        if (!selectedId) return null;

        // First check if the item is already in loaded data
        const foundInLoadedData = flatData.find((doc) => doc._id === selectedId);
        if (foundInLoadedData) return foundInLoadedData;

        // If not found in loaded data, use specific feedback data
        if (specificFeedbackData) {
            return {
                ...specificFeedbackData,
                fields: specificFeedbackData._source?.metadata,
            };
        }

        return null;
    }, [selectedId, flatData, specificFeedbackData]);

    // Handle row click - update URL with ID
    const handleRowClick = useCallback(
        (row: FeedbackSchema) => {
            router.navigate({
                to: "/sme",
                // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                search: { id: row._id } as any,
            });
        },
        [router],
    );

    // Handle modal close - remove ID from URL
    const handleModalClose = useCallback(() => {
        router.navigate({
            to: "/sme",
            // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
            search: { id: undefined } as any,
        });
    }, [router]);

    // Handle successful feedback submission
    const handleFeedbackSuccess = useCallback(() => {
        // Invalidate count queries to refresh counts
        queryClient.invalidateQueries({ queryKey: ["sme-feedback-count"] });
        // Delay the main data refresh to prevent modal closing/reopening
        setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ["sme-feedback"] });
        }, 300);
        // Don't switch tabs - just stay on current filter and navigate to next
    }, [queryClient]);

    // Navigation logic for Next button
    const currentIndex = useMemo(() => {
        if (!selectedId || !flatData) return -1;
        return flatData.findIndex((item) => item._id === selectedId);
    }, [selectedId, flatData]);

    const hasNext = currentIndex >= 0 && currentIndex < flatData.length - 1;
    const hasPrevious = currentIndex > 0;

    const handleNext = useCallback(() => {
        if (hasNext && flatData) {
            const nextItem = flatData[currentIndex + 1];

            // Load more data if approaching end of current data and we have a valid way to fetch more
            const remainingItems = flatData.length - currentIndex - 1;
            if (
                remainingItems <= 5 &&
                flatData.length < totalDBRowCount &&
                !isFetching &&
                documentId
            ) {
                fetchNextPage();
            }

            router.navigate({
                to: "/sme",
                // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                search: { id: nextItem._id } as any,
            });
        }
    }, [
        hasNext,
        flatData,
        currentIndex,
        router,
        totalDBRowCount,
        isFetching,
        fetchNextPage,
        documentId,
    ]);

    const handlePrevious = useCallback(() => {
        if (hasPrevious && flatData) {
            const previousItem = flatData[currentIndex - 1];
            router.navigate({
                to: "/sme",
                // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                search: { id: previousItem._id } as any,
            });
        }
    }, [hasPrevious, flatData, currentIndex, router]);

    // Custom toolbar with segmented control and admin-only email input
    const CustomToolbar = () => (
        <Flex justify="space-between" align="center" w="100%">
            <TopToolbarCustomActions
                // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                table={table as any}
                index={CTS_USER_CHAT}
                // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                data={data as any}
                columnFilters={columnFilters}
                // TODO This is a required prop but was not set previously, so it is currently set to false,
                // but needs to be revisited.
                isLoading={false}
            />

            {/* Admin-only email input for reviewing other users */}
            {/* There was a request to disable the review user email even for admins for now. Did this by putting false &&*/}
            {false && isAdmin && (
                <Flex align="center" gap="md">
                    <Text size="sm" fw={500}>
                        Review User:
                    </Text>
                    <TextInput
                        placeholder="Enter email to review..."
                        value={reviewUserEmail}
                        onChange={(event) => handleReviewUserEmail(event.currentTarget.value)}
                        size="sm"
                        w={250}
                        styles={(theme) => ({
                            input: {
                                borderColor: reviewUserEncoded ? theme.colors.green[6] : undefined,
                            },
                        })}
                        rightSection={
                            reviewUserEmail ? (
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    onClick={clearReviewUser}
                                    title="Clear review user"
                                >
                                    <IconX size={12} />
                                </ActionIcon>
                            ) : undefined
                        }
                    />
                    {reviewUserEmail && (
                        <Text size="xs" color={reviewUserEncoded ? "green" : "red"}>
                            {reviewUserEncoded ? "✓ Valid" : "✗ Invalid email"}
                        </Text>
                    )}
                </Flex>
            )}

            {/* My Questions Toggle */}
            <Flex align="center" gap="md">
                <Switch
                    label="My Questions"
                    checked={myQuestionsOnly}
                    onChange={(event) => setMyQuestionsOnly(event.currentTarget.checked)}
                    size="sm"
                />
            </Flex>

            <Flex align="center" gap="md" mr="md">
                <Text size="sm" color="dimmed">
                    Total: {displayCount}{" "}
                    {flatData.length < displayCount ? `(showing ${flatData.length})` : ""}
                </Text>
                {reviewUserEncoded && (
                    <Text size="xs" color="blue" fw={500}>
                        Reviewing: {reviewUserEmail}
                    </Text>
                )}
                <SegmentedControl
                    value={filterMode}
                    onChange={(value) => {
                        // Update filter mode - this will trigger refetch due to queryKey dependency
                        setFilterMode(value);
                    }}
                    data={[
                        { label: "To Review", value: "to_review" },
                        { label: "Reviewed", value: "reviewed" },
                    ]}
                    size="sm"
                />
            </Flex>
        </Flex>
    );

    const table = useMantineReactTable<FeedbackSchema>({
        layoutMode: "grid",
        enableGlobalFilter: false,
        columns,
        data: flatData ?? [],
        enableColumnOrdering: true,
        enablePinning: true,
        enableBottomToolbar: false,
        renderToolbarAlertBannerContent: () => <></>,
        enableColumnResizing: true,
        enableColumnFilterModes: false,
        // TODO This used to specify a filter function, but this property doesn't actually exist.
        // It should be converted into using `filterFns`.
        // filterFn: "includesString",
        enablePagination: false,
        enableRowNumbers: true,
        enableClickToCopy: false,
        getRowId: (row, index) => {
            // Use _id if available, otherwise create a stable ID based on row content
            if (row._id) return row._id;
            // Create a more stable fallback ID using timestamp and question
            const timestamp = row._source?.timestamp || "";
            const question = row._source?.question || "";
            return `row-${index}-${timestamp}-${question}`.replace(/[^a-zA-Z0-9-]/g, "");
        },
        renderTopToolbarCustomActions: CustomToolbar,
        mantineToolbarAlertBannerProps: {
            style: {
                display: "none",
                background: "#f0f0f0",
                fontWeight: "bold",
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        enableFullScreenToggle: false,
        enableSelectAll: false,
        selectAllMode: "page",
        enableGrouping: true,
        enableRowVirtualization: true,
        enableDensityToggle: false,
        mantineTableBodyRowProps: ({ row }) => ({
            onClick: () => handleRowClick(row.original as FeedbackSchema),
            style: {
                cursor: "pointer",
            },
        }),
        mantineTableContainerProps: {
            ref: tableContainerRef, //get access to the table container element
            style: {
                padding: 0,
                borderRadius: 0,
                maxHeight: "80vh",
                overflowX: "auto",
            },
            onScroll: (
                event: UIEvent<HTMLDivElement>, //add an event listener to the table container element
            ) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
        },
        mantinePaperProps: { style: { borderRadius: 0, zIndex: 102 } },
        onSortingChange: setSorting,
        state: {
            isLoading,
            sorting,
            columnFilters,
            globalFilter,
        },
        initialState: {
            showGlobalFilter: true,
            density: "xs",
            showColumnFilters: true,
        },
        // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
        rowVirtualizerInstanceRef: ((instance: any) => {
            setRowVirtualizerRef(instance);
            // biome-ignore lint/suspicious/noExplicitAny: Required for MRT typing
        }) as any,
        rowVirtualizerOptions: {
            overscan: 10,
            // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
        } as any,
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: sorting and rowVirtualizerRef are required
    useEffect(() => {
        if (rowVirtualizerRef) {
            rowVirtualizerRef.scrollToIndex(0);
        }
    }, [sorting, rowVirtualizerRef]);

    // Reset scroll when filter mode changes
    // biome-ignore lint/correctness/useExhaustiveDependencies: filterMode is intentionally included
    useEffect(() => {
        // Reset the table container scroll position
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollTop = 0;
        }
        // Reset virtualizer to top
        if (rowVirtualizerInstanceRef.current) {
            try {
                rowVirtualizerInstanceRef.current.scrollToIndex(0);
            } catch (e) {
                console.error(e);
            }
        }
    }, [filterMode]);

    // Reset scroll when switching review user (admin-only)
    // biome-ignore lint/correctness/useExhaustiveDependencies: activeUserEncoded is intentionally included
    useEffect(() => {
        // Reset the table container scroll position
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollTop = 0;
        }
        // Reset virtualizer to top
        if (rowVirtualizerInstanceRef.current) {
            try {
                rowVirtualizerInstanceRef.current.scrollToIndex(0);
            } catch (e) {
                console.error(e);
            }
        }
    }, [activeUserEncoded]);

    return (
        <PdfFocusProvider>
            <MantineReactTable table={table} />

            <SMEFeedbackModal
                rowData={selectedRow}
                onClose={handleModalClose}
                onSuccess={handleFeedbackSuccess}
                onNext={handleNext}
                onPrevious={handlePrevious}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                flatData={flatData}
                currentIndex={currentIndex}
            />
        </PdfFocusProvider>
    );
};

export default SMEFeedback;
